import React, { useState, useRef, useCallback } from 'react';
import { useMediaUpload, MEDIA_TYPES, UploadProgress } from '../../hooks/useMediaUpload';
import { Upload, Image, Crop, Filter, Music, Type, Sticker, ArrowRight, Trash2, ChevronLeft, ChevronRight, FileText, Lock, MapPin, Eye } from 'lucide-react';
import Modal from './common/Modal';
import AudioSearchDialog from './common/AudioSearchDialog';
import PreviewPopup from './common/PreviewPopup';
import './PhotoUploadPopup.css';

interface PhotoUploadPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (data: PhotoUploadData) => void;
}

export interface PhotoItem {
  id: string;
  file: File;
  preview: string;
  description: string;
  cropData?: any;
  filterData?: any;
  textOverlays?: any[];
  stickers?: any[];
  audioData?: { url: string; name: string; volume: number; originalVolume: number; trimStart?: number; trimEnd?: number };
}

export interface PhotoUploadData {
  photos: PhotoItem[];
  description: string;
  privacy: 'public' | 'friends' | 'private';
  location: string;
}

type UploadStep = 'select' | 'edit' | 'details' | 'publish';
type EditMode = 'crop' | 'filter' | 'audio' | 'text' | 'sticker' | null;

const MAX_PHOTOS = 15;

const PhotoUploadPopup: React.FC<PhotoUploadPopupProps> = ({ isOpen, onClose, onSubmit }) => {
  // Add Supabase upload hook with proper destructuring
  const { uploadMultiple, isUploading: isUploadingMedia, error: uploadError, progress } = useMediaUpload();
  
  // Main workflow state
  const [currentStep, setCurrentStep] = useState<UploadStep>('select');
  const [editMode, setEditMode] = useState<EditMode>(null);
  
  // Photo data
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  
  // Form data
  const [description, setDescription] = useState('');
  const [privacy, setPrivacy] = useState<'public' | 'friends' | 'private'>('public');
  const [location, setLocation] = useState('');
  
  // UI state
  const [dragActive, setDragActive] = useState(false);
  const [showAudioSearch, setShowAudioSearch] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  
  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentPhoto = photos[currentPhotoIndex];

  // File handling
  const handleFileChange = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;

    const filesArray = Array.from(files).filter(file => file.type.startsWith('image/'));
    const availableSlots = MAX_PHOTOS - photos.length;
    const filesToAdd = filesArray.slice(0, availableSlots);
    
    if (filesToAdd.length === 0) return;

    const newPhotoPromises = filesToAdd.map((file, index) => {
      return new Promise<PhotoItem>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve({
            id: `photo-${Date.now()}-${index}`,
            file,
            preview: reader.result as string,
            description: ''
          });
        };
        reader.readAsDataURL(file);
      });
    });

    Promise.all(newPhotoPromises).then(newPhotos => {
      setPhotos(prev => [...prev, ...newPhotos]);
      if (photos.length === 0) {
        setCurrentStep('edit');
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
    });
  }, [photos.length]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    handleFileChange(e.dataTransfer.files);
  }, [handleFileChange]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  // Navigation
  const handleNext = () => {
    if (currentStep === 'select' && photos.length > 0) {
      setCurrentStep('edit');
    } else if (currentStep === 'edit') {
      setCurrentStep('details');
    } else if (currentStep === 'details') {
      setCurrentStep('publish');
    }
  };

  const handleBack = () => {
    if (currentStep === 'details') {
      setCurrentStep('edit');
    } else if (currentStep === 'edit') {
      setCurrentStep('select');
    } else if (currentStep === 'publish') {
      setCurrentStep('details');
    }
  };

  // Photo management
  const removePhoto = (index: number) => {
    setPhotos(prev => {
      const newPhotos = [...prev];
      newPhotos.splice(index, 1);
      
      if (index <= currentPhotoIndex && currentPhotoIndex > 0) {
        setCurrentPhotoIndex(currentPhotoIndex - 1);
      }
      
      if (newPhotos.length === 0) {
        setCurrentStep('select');
      }
      
      return newPhotos;
    });
  };

  const handlePrevPhoto = () => {
    setCurrentPhotoIndex(prev => Math.max(0, prev - 1));
  };

  const handleNextPhoto = () => {
    setCurrentPhotoIndex(prev => Math.min(photos.length - 1, prev + 1));
  };

  // Audio handling
  const handleAudioSelect = (audio: any) => {
    if (!currentPhoto) return;
    
    const audioData = {
      url: audio.url || audio.audioUrl,
      name: audio.name || `${audio.title} - ${audio.artist}`,
      volume: 0.8,
      originalVolume: 0.2,
      trimStart: audio.trimStart,
      trimEnd: audio.trimEnd
    };
    
    setPhotos(prev => prev.map(photo => 
      photo.id === currentPhoto.id 
        ? { ...photo, audioData }
        : photo
    ));
    
    setShowAudioSearch(false);
    setEditMode(null);
  };

  // Form submission with enhanced error handling
  const handleSubmit = async () => {
    if (photos.length === 0) return;
    
    try {
      console.log('🚀 Starting photo upload...', { 
        photoCount: photos.length,
        mediaType: MEDIA_TYPES.POST_IMAGE,
        isUploadingMedia,
        uploadError 
      });
      
      // Upload all photos to Supabase storage
      const photoFiles = photos.map(photo => photo.file);
      const uploadResults = await uploadMultiple(photoFiles, MEDIA_TYPES.POST_IMAGE);
      
      console.log('📤 Upload results:', uploadResults);
      console.log('📊 Upload progress:', progress);
      console.log('❌ Upload error:', uploadError);
      
      // Check if all uploads were successful
      const allUploadsSuccessful = uploadResults.every(result => result.success);
      
      if (!allUploadsSuccessful) {
        const errors = uploadResults
          .filter(result => !result.success)
          .map(result => result.error)
          .join(', ');
        console.error('❌ Upload failed:', errors);
        throw new Error(`Some uploads failed: ${errors}`);
      }
      
      console.log('✅ All uploads successful!');
      
      // Create enhanced photo data with URLs and keys
      const enhancedPhotos = photos.map((photo, index) => ({
        ...photo,
        mediaUrl: uploadResults[index].url,
        mediaKey: uploadResults[index].key
      }));
      
      const photoData: PhotoUploadData = {
        photos: enhancedPhotos,
        description,
        privacy,
        location
      };
      
      console.log('📊 Final photo data:', photoData);
      
      if (onSubmit) {
        await onSubmit(photoData);
      } else {
        console.log('⚠️ No onSubmit handler provided, logging data only');
      }
      
      // Reset form
      setPhotos([]);
      setDescription('');
      setPrivacy('public');
      setLocation('');
      setCurrentStep('select');
      setCurrentPhotoIndex(0);
      setShowPreview(false);
      onClose();
    } catch (error) {
      console.error('💥 Upload failed:', error);
      console.error('💥 Upload error state:', uploadError);
      console.error('💥 Is uploading:', isUploadingMedia);
      // Don't close the popup on error so user can retry
    }
  };

  const resetForm = () => {
    setPhotos([]);
    setDescription('');
    setPrivacy('public');
    setLocation('');
    setCurrentStep('select');
    setCurrentPhotoIndex(0);
    setEditMode(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Step indicators
  const steps = [
    { id: 'select', label: 'Select', icon: Upload },
    { id: 'edit', label: 'Edit', icon: Image },
    { id: 'details', label: 'Details', icon: FileText },
    { id: 'publish', label: 'Publish', icon: ArrowRight }
  ];

  const currentStepIndex = steps.findIndex(step => step.id === currentStep);

  // Preview data preparation
  const getPreviewData = () => ({
    description,
    privacy,
    location,
    photos: photos.map(photo => ({
      id: photo.id,
      preview: photo.preview,
      description: photo.description,
      audioData: photo.audioData
    }))
  });

  return (
    <Modal isOpen={isOpen} onClose={() => { resetForm(); onClose(); }} title="Create Photo Post" size="xl">
      <div className="flex flex-col h-[80vh] max-h-[600px]">
        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-6 px-4">
          {steps.map((step, index) => {
            const StepIcon = step.icon;
            const isActive = index === currentStepIndex;
            const isCompleted = index < currentStepIndex;
            
            return (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 transition-colors ${
                  isActive 
                    ? 'border-blue-600 bg-blue-600 text-white' 
                    : isCompleted 
                    ? 'border-green-600 bg-green-600 text-white'
                    : 'border-gray-300 bg-white text-gray-400'
                }`}>
                  <StepIcon size={16} />
                </div>
                <span className={`ml-2 text-sm font-medium ${
                  isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-400'
                }`}>
                  {step.label}
                </span>
                {index < steps.length - 1 && (
                  <div className={`w-8 h-0.5 mx-4 ${
                    isCompleted ? 'bg-green-600' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            );
          })}
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-hidden">
          {/* Step 1: Select Photos */}
          {currentStep === 'select' && (
            <div className="h-full flex flex-col">
              <div className="flex-1 flex items-center justify-center p-8">
                {photos.length === 0 ? (
                  <div 
                    className={`w-full max-w-md border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
                      dragActive 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
                    }`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Upload Photos</h3>
                    <p className="text-gray-600 mb-4">Drag and drop or click to select up to {MAX_PHOTOS} photos</p>
                    <p className="text-sm text-gray-500">PNG, JPG, GIF up to 10MB each</p>
                  </div>
                ) : (
                  <div className="w-full max-w-4xl">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {photos.map((photo, index) => (
                        <div key={photo.id} className="relative group">
                          <img 
                            src={photo.preview} 
                            alt={`Photo ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg"
                          />
                          <button
                            onClick={() => removePhoto(index)}
                            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                      {photos.length < MAX_PHOTOS && (
                        <div 
                          className="h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-blue-400 transition-colors"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <Upload className="h-8 w-8 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <p className="text-center text-sm text-gray-500 mt-4">
                      {photos.length} of {MAX_PHOTOS} photos selected
                    </p>
                  </div>
                )}
                <input 
                  type="file" 
                  accept="image/*" 
                  ref={fileInputRef} 
                  onChange={(e) => handleFileChange(e.target.files)} 
                  className="hidden" 
                  multiple 
                />
              </div>
            </div>
          )}

          {/* Step 2: Edit Photos */}
          {currentStep === 'edit' && currentPhoto && (
            <div className="h-full flex flex-col">
              {/* Photo Preview */}
              <div className="flex-1 flex items-center justify-center bg-gray-100 rounded-xl mx-4 mb-4 relative">
                <img 
                  src={currentPhoto.preview} 
                  alt="Current photo"
                  className="max-h-full max-w-full object-contain rounded-xl"
                />
                
                {/* Navigation arrows */}
                {photos.length > 1 && (
                  <>
                    <button 
                      onClick={handlePrevPhoto}
                      disabled={currentPhotoIndex === 0}
                      className={`absolute left-4 p-2 rounded-full bg-black/30 text-white transition-opacity ${
                        currentPhotoIndex === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-black/50'
                      }`}
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button 
                      onClick={handleNextPhoto}
                      disabled={currentPhotoIndex === photos.length - 1}
                      className={`absolute right-4 p-2 rounded-full bg-black/30 text-white transition-opacity ${
                        currentPhotoIndex === photos.length - 1 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-black/50'
                      }`}
                    >
                      <ChevronRight size={20} />
                    </button>
                  </>
                )}
                
                {/* Photo counter */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                  {currentPhotoIndex + 1} of {photos.length}
                </div>
              </div>

              {/* Edit Tools */}
              <div className="px-4 pb-4">
                <div className="grid grid-cols-5 gap-3">
                  <button
                    onClick={() => setEditMode(editMode === 'crop' ? null : 'crop')}
                    className={`edit-tool-btn ${editMode === 'crop' ? 'active' : ''}`}
                  >
                    <Crop size={20} />
                    <span>Crop</span>
                  </button>
                  <button
                    onClick={() => setEditMode(editMode === 'filter' ? null : 'filter')}
                    className={`edit-tool-btn ${editMode === 'filter' ? 'active' : ''}`}
                  >
                    <Filter size={20} />
                    <span>Filter</span>
                  </button>
                  <button
                    onClick={() => setEditMode(editMode === 'audio' ? null : 'audio')}
                    className={`edit-tool-btn ${editMode === 'audio' ? 'active' : ''}`}
                  >
                    <Music size={20} />
                    <span>Audio</span>
                  </button>
                  <button
                    onClick={() => setEditMode(editMode === 'text' ? null : 'text')}
                    className={`edit-tool-btn ${editMode === 'text' ? 'active' : ''}`}
                  >
                    <Type size={20} />
                    <span>Text</span>
                  </button>
                  <button
                    onClick={() => setEditMode(editMode === 'sticker' ? null : 'sticker')}
                    className={`edit-tool-btn ${editMode === 'sticker' ? 'active' : ''}`}
                  >
                    <Sticker size={20} />
                    <span>Sticker</span>
                  </button>
                </div>

                {/* Edit Mode Panels */}
                {editMode === 'crop' && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                    <p className="text-gray-600 mb-3">Crop tool will be implemented here</p>
                    <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                      Apply Crop
                    </button>
                  </div>
                )}

                {editMode === 'filter' && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                    <p className="text-gray-600 mb-3">Filter options will be implemented here</p>
                    <div className="grid grid-cols-4 gap-2">
                      {['Original', 'Vintage', 'B&W', 'Sepia'].map(filter => (
                        <button key={filter} className="p-2 text-sm bg-white rounded border hover:bg-gray-50">
                          {filter}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {editMode === 'audio' && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-gray-700 font-medium">Background Audio</span>
                      <button
                        onClick={() => setShowAudioSearch(true)}
                        className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
                      >
                        Browse Audio
                      </button>
                    </div>
                    {currentPhoto.audioData && (
                      <div className="flex items-center justify-between p-3 bg-white rounded border">
                        <span className="text-sm text-gray-700">{currentPhoto.audioData.name}</span>
                        <button
                          onClick={() => setPhotos(prev => prev.map(photo => 
                            photo.id === currentPhoto.id 
                              ? { ...photo, audioData: undefined }
                              : photo
                          ))}
                          className="text-red-500 hover:text-red-700"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {editMode === 'text' && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                    <p className="text-gray-600 mb-3">Text overlay tool will be implemented here</p>
                    <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                      Add Text
                    </button>
                  </div>
                )}

                {editMode === 'sticker' && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                    <p className="text-gray-600 mb-3">Sticker panel will be implemented here</p>
                    <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                      Add Sticker
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Details */}
          {currentStep === 'details' && (
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Write a caption for your photos..."
                    />
                  </div>

                  {/* Privacy */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Privacy</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <select
                        value={privacy}
                        onChange={(e) => setPrivacy(e.target.value as 'public' | 'friends' | 'private')}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="public">Public</option>
                        <option value="friends">Friends</option>
                        <option value="private">Private (Only Me)</option>
                      </select>
                    </div>
                  </div>

                  {/* Location */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Location (Optional)</label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., New York, NY"
                      />
                    </div>
                  </div>
                </div>

                {/* Preview */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-gray-700">Preview</h3>
                  <div className="bg-gray-100 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {photos.slice(0, 4).map((photo, index) => (
                        <img 
                          key={photo.id}
                          src={photo.preview} 
                          alt={`Preview ${index + 1}`}
                          className="w-full h-20 object-cover rounded"
                        />
                      ))}
                      {photos.length > 4 && (
                        <div className="w-full h-20 bg-gray-200 rounded flex items-center justify-center text-gray-500 text-sm">
                          +{photos.length - 4} more
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">{photos.length} photos</p>
                    {description && (
                      <p className="text-sm text-gray-800 mt-2">{description}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Publish */}
          {currentStep === 'publish' && (
            <div className="p-6 text-center">
              <div className="max-w-md mx-auto">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Ready to Publish</h3>
                <p className="text-gray-600 mb-6">
                  Your photo post with {photos.length} photos is ready to be shared.
                </p>
                
                {/* Preview Button */}
                <button
                  onClick={() => setShowPreview(true)}
                  className="w-full px-6 py-3 mb-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center"
                >
                  <Eye className="mr-2" size={20} />
                  Preview Post
                </button>
                
                <button
                  onClick={handleSubmit}
                  disabled={isUploadingMedia}
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                >
                  {isUploadingMedia ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Publishing...
                    </>
                  ) : (
                    'Publish Photos'
                  )}
                </button>
                {uploadError && (
                  <p className="text-red-500 mt-2">{uploadError}</p>
                )}
                {progress && (
                  <p className="text-gray-600 mt-2">Uploading... {progress.percentage}%</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Navigation Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200">
          <button
            onClick={handleBack}
            disabled={currentStep === 'select'}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            Back
          </button>
          
          <div className="flex space-x-2">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentStepIndex ? 'bg-blue-600' : index < currentStepIndex ? 'bg-green-600' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
          
          <button
            onClick={handleNext}
            disabled={
              (currentStep === 'select' && photos.length === 0) ||
              currentStep === 'publish'
            }
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {currentStep === 'details' ? 'Review' : 'Next'}
          </button>
        </div>
      </div>

      {/* Audio Search Dialog */}
      {showAudioSearch && (
        <AudioSearchDialog
          isOpen={showAudioSearch}
          onClose={() => setShowAudioSearch(false)}
          onSelectAudio={handleAudioSelect}
        />
      )}

      {/* Preview Popup */}
      {showPreview && (
        <PreviewPopup
          isOpen={showPreview}
          onClose={() => setShowPreview(false)}
          onConfirm={handleSubmit}
          type="photo"
          data={getPreviewData()}
        />
      )}
    </Modal>
  );
};

export default PhotoUploadPopup;
